import { useCallback, useEffect, useState } from "react";
import {
  approveDemandeEntree,
  getDemandesEntree,
  rejectDemandeEntree,
  type DemandeEntreeItem,
} from "../../../api/etablissement";
import { showToast } from "../../../components/ui/toastHotSonner";

const PAGE_SIZE = 20;

function formatDateFR(input?: string | null): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleDateString("fr-FR");
}

type Props = { etablissementId: string; onApproved: () => void };

export default function DemandesEntree({ etablissementId, onApproved }: Props) {
  const [items, setItems] = useState<DemandeEntreeItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!etablissementId) {
      setLoading(false);
      setError("Identifiant établissement indisponible.");
      return;
    }
    setLoading(true);
    setError(null);
    const res = await getDemandesEntree(etablissementId, page, PAGE_SIZE);
    if (!res.ok) {
      setError(res.message);
      setLoading(false);
      return;
    }
    setItems(res.data);
    setTotalPages(res.pagination.totalPages || 1);
    setLoading(false);
  }, [etablissementId, page]);

  useEffect(() => { void load(); }, [load]);

  const act = async (liaisonId: string, action: "approve" | "reject") => {
    setActingId(liaisonId);
    const res =
      action === "approve"
        ? await approveDemandeEntree(etablissementId, liaisonId)
        : await rejectDemandeEntree(etablissementId, liaisonId);
    setActingId(null);
    if (!res.ok) {
      if (res.status === 404) showToast("Cette demande n'existe plus.", "error");
      else if (res.status === 403) showToast("Cette liaison n'appartient pas à votre établissement.", "error");
      else showToast(res.message, "error");
      await load();
      return;
    }
    showToast(action === "approve" ? "Demande approuvée." : "Demande refusée.", "success");
    setItems((prev) => prev.filter((i) => i.liaison.id !== liaisonId));
    if (action === "approve") onApproved();
  };

  return (
    <div className="space-y-4">
      {loading && <div className="py-12 text-center text-gray-600">Chargement…</div>}

      {!loading && error && (
        <div className="rounded-[10px] border p-6 text-center" style={{ borderColor: "#E5E7EB" }}>
          <p className="text-gray-700">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 h-10 px-4 rounded-[10px] font-medium"
            style={{ backgroundColor: "#00553E", color: "#fff" }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-[10px] border" style={{ borderColor: "#E5E7EB" }}>
          <div className="hidden md:grid md:grid-cols-12 gap-x-6 px-6 py-4 font-semibold text-gray-700">
            <div className="md:col-span-3">Professionnel</div>
            <div className="md:col-span-3">Spécialité</div>
            <div className="md:col-span-2">Poste demandé</div>
            <div className="md:col-span-2">Depuis</div>
            <div className="md:col-span-2 text-right">Actions</div>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {items.length === 0 ? (
              <div className="text-gray-500 px-3 py-6 text-center">Aucune demande en attente pour le moment.</div>
            ) : (
              items.map((item) => (
                <div
                  key={item.liaison.id}
                  className="bg-gray-100 rounded-[10px] p-3 md:px-4 md:py-2 md:min-h-14 flex flex-col gap-2 md:grid md:grid-cols-12 md:gap-x-6 md:items-center"
                >
                  <div className="md:col-span-3">
                    <div className="font-medium text-[#0B1F16]">
                      {item.professionnel.nom} {item.professionnel.prenom}
                    </div>
                    <div className="text-xs text-gray-500">NIP {item.professionnel.nip}</div>
                  </div>
                  <div className="md:col-span-3 text-sm text-gray-700">{item.professionnel.specialite ?? "—"}</div>
                  <div className="md:col-span-2 text-sm text-gray-700">{item.liaison.poste ?? "—"}</div>
                  <div className="md:col-span-2 text-sm text-gray-700">{formatDateFR(item.liaison.dateDebut) || "—"}</div>
                  <div className="md:col-span-2 flex gap-2 md:justify-end">
                    <button
                      type="button"
                      onClick={() => void act(item.liaison.id, "approve")}
                      disabled={actingId === item.liaison.id}
                      className="h-9 px-3 rounded-[10px] font-semibold text-white text-sm disabled:opacity-60"
                      style={{ background: "#00553E" }}
                    >
                      Approuver
                    </button>
                    <button
                      type="button"
                      onClick={() => void act(item.liaison.id, "reject")}
                      disabled={actingId === item.liaison.id}
                      className="h-9 px-3 rounded-[10px] border text-sm text-red-600 disabled:opacity-60"
                      style={{ borderColor: "#F3C0C0" }}
                    >
                      Refuser
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t text-sm" style={{ borderColor: "#E5E7EB" }}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                ← Précédent
              </button>
              <span className="text-gray-500">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                Suivant →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
